import { injectable } from '@patsnap/tahiti-core/shared/inversify';

export interface QueueMapping<ExecuteItem extends Function> {
    [key: string]: QueueItem<ExecuteItem>[];
}

export interface QueueItem<ExecuteItem extends Function> {
    index: number;
    execute: ExecuteItem;
}

export interface QueueService<ExecuteItem extends Function> {
    pushQueue(key: string, executeItem: ExecuteItem): QueueItem<ExecuteItem>;
    executeQueue<T>(key: string, queueItem: QueueItem<ExecuteItem>, callback: (result: T) => void): Promise<void>;
}

export const QueueService = Symbol('QueueService');

@injectable()
export abstract class AbstractQueueService<ExecuteItem extends Function> implements QueueService<ExecuteItem> {
    private _queueMapping: QueueMapping<ExecuteItem> = {};

    public pushQueue(key: string, executeItem: ExecuteItem): QueueItem<ExecuteItem> {
        const currentExecute: QueueItem<ExecuteItem> = { index: 0, execute: executeItem };
        if (!this._queueMapping[key]) {
            this._queueMapping[key] = [currentExecute];
        } else {
            currentExecute.index = this._queueMapping[key].length;
            this._queueMapping[key].push(currentExecute);
        }
        return currentExecute;
    }

    public async executeQueue<T>(key: string, currentExecute: QueueItem<ExecuteItem>, callback: (result: T) => void) {
        const result: T = await currentExecute.execute();
        if (this._getIsLatestInQueue(key, currentExecute.index)) {
            callback(result);
            this._clearQueue(key);
        }
    }

    private _clearQueue(key: string) {
        this._queueMapping[key] = [];
    }

    private _getIsLatestInQueue(key: string, index: number): boolean {
        return index === this._queueMapping[key].length - 1;
    }
}
